import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";
import FraudPlayersFilter from "./FraudPlayersFilter";
import FraudPlayersItem from "./FraudPlayersItem";
import {
  DEFAULT_FRAUD_PLAYERS_SORT,
  sortFraudPlayersRows,
} from "./fraudPlayersSort";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import {
  selectBizMobileBeAwareFraudPlayersLoading,
  selectBizMobileBeAwareFraudPlayersOutput,
} from "../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { exportBeAwareTableToCSV } from "../../../../../../DashboardDesktop/BeAware/beAwareCsvHelpers";

// CSV column order/titles — identical to Fraud Players Desktop.
const tableHeadings = {
  brand_name: "Brand Name",
  brand_id: "Brand ID",
  total_income: "Total Income",
  cpa: "CPA",
  rs: "RS",
  cpa_from_total_income: "% CPA from Total Income",
  rs_from_total_income: "% RS from Total Income",
  qftd: "QFTD",
  ftd: "FTD",
};

// Keep raw values; empty string when missing so the CSV shows a blank cell.
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);
// Round monetary values to 2 decimals; empty string when missing/non-numeric.
const money = (value: any) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : String(Math.round(n * 100) / 100);
};
// Calculated in client: (numerator / denominator) * 100, rounded to a whole
// number and suffixed with "%" (e.g. 108.73 -> "109%").
const percentOf = (numerator: any, denominator: any) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return "";
  return `${Math.round((n / d) * 100)}%`;
};
// Monetary columns prefixed with "$" in the CSV (mirrors Desktop).
const MONEY_KEYS = new Set(["total_income", "cpa", "rs"]);
// CSV cell resolver: prefix monetary columns with "$" to match Desktop.
const getFraudPlayersCellValue = (key: string, row: any) => {
  const value = row[key] ?? "";
  return MONEY_KEYS.has(key) && value !== "" ? `$${value}` : value;
};

const FraudPlayersSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Stack spacing={2}>
      {[1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{ borderRadius: 2, border: "1px solid #e0e0e0", p: 2 }}
        >
          <Stack spacing={1.2}>
            <Skeleton width={140} height={20} />
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={160} height={18} />
              <Skeleton width={80} height={24} />
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={120} height={18} />
              <Skeleton width={120} height={18} />
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
);

const FraudPlayers = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");
  const isLoading = useSelector(selectBizMobileBeAwareFraudPlayersLoading);
  const output = useSelector(selectBizMobileBeAwareFraudPlayersOutput);
  const { fetchBeAwareFraudPlayersData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();
  // Brand Name search — mirrors Budget Control Card View (substring match).
  const [brandName, setBrandName] = useState("");
  const [sortKey, setSortKey] = useState(DEFAULT_FRAUD_PLAYERS_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_FRAUD_PLAYERS_SORT.dir,
  );

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchBeAwareFraudPlayersData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  // Unique, non-empty brand names used as the search options.
  const brandList = useMemo(() => {
    const set = new Set<string>();
    (output ?? []).forEach((row: Record<string, any>) => {
      const name = String(row?.brand_display_name ?? "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [output]);

  // Rows shown in the cards (and exported) respect the brand search.
  const filteredOutput = useMemo(() => {
    const search = brandName.trim().toLowerCase();
    if (!search) return output ?? [];
    return (output ?? []).filter((row: Record<string, any>) =>
      String(row?.brand_display_name ?? "")
        .toLowerCase()
        .includes(search),
    );
  }, [output, brandName]);

  // Sorted rows shared by the cards and the CSV download.
  const sortedOutput = useMemo(
    () => sortFraudPlayersRows(filteredOutput, sortKey, sortDir),
    [filteredOutput, sortKey, sortDir],
  );

  // CSV rows — mapped from the same API keys as Fraud Players Desktop.
  const csvRows = useMemo(
    () =>
      sortedOutput.map((item: Record<string, any>) => ({
        brand_name: raw(item.brand_display_name),
        brand_id: raw(item.brand_id),
        total_income: money(item.total_income),
        cpa: money(item.cpa),
        rs: money(item.revshare),
        cpa_from_total_income: percentOf(item.cpa, item.total_income),
        rs_from_total_income: percentOf(item.revshare, item.total_income),
        qftd: raw(item.qftd),
        ftd: raw(item.ftd),
      })),
    [sortedOutput],
  );

  const brandCount = filteredOutput.length;

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows: csvRows,
      filename: "fraud_players",
      getCellValue: getFraudPlayersCellValue,
    });
  };

  return (
    <>
      <Box sx={{ px: 2 }}>
        <FraudPlayersFilter
          brandCount={brandCount}
          onDownload={handleDownload}
          downloadDisabled={isLoading || brandCount === 0}
          sortKey={sortKey}
          sortDir={sortDir}
          onApplySort={handleApplySort}
          brandList={brandList}
          brandName={brandName}
          onBrandNameChange={setBrandName}
        />
        {isLoading ? (
          <FraudPlayersSkeleton />
        ) : (
          <FraudPlayersItem data={sortedOutput} />
        )}
      </Box>
    </>
  );
};

export default FraudPlayers;
